import { memo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { Spawn } from "@cimara/protocol";
import type { World } from "@cimara/world";
import type { RideStore } from "../ride/store";
import { Course } from "./Course";
import { CameraRig } from "./CameraRig";
import { Riders } from "./Riders";
import { SkyDome } from "./SkyDome";
import { FOG_COLOR, SUN_COLOR } from "./palette";

function SimulationClock({ store }: { store: RideStore }) {
  useFrame((_state, delta) => {
    store.frame(Date.now(), delta);
  }, -10);
  return null;
}

function SceneContents({ store, world, routeId, startSpawn }: SceneProps) {
  return (
    <>
      <color attach="background" args={[FOG_COLOR]} />
      <fog attach="fog" args={[FOG_COLOR, 150, 1380]} />
      <hemisphereLight args={["#e7f4e9", "#263f2f", 1.55]} />
      <directionalLight position={[-120, 180, 90]} intensity={2.1} color={SUN_COLOR} />
      <SimulationClock store={store} />
      <SkyDome />
      <Course world={world} routeId={routeId} startSpawn={startSpawn} />
      <Riders store={store} />
      <CameraRig store={store} />
    </>
  );
}

interface SceneProps {
  store: RideStore;
  world: World;
  routeId: number | undefined;
  startSpawn: Spawn;
}

export const RideScene = memo(function RideScene({
  store,
  world,
  routeId,
  startSpawn,
}: SceneProps) {
  return (
    <Canvas
      className="ride-canvas"
      dpr={[1, 1.35]}
      gl={{ antialias: true, powerPreference: "high-performance", alpha: false }}
      camera={{ fov: 62, near: 0.4, far: 4000, position: [0, 40, 0] }}
      onCreated={({ gl }) => {
        gl.outputColorSpace = THREE.SRGBColorSpace;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.02;
      }}
    >
      <SceneContents store={store} world={world} routeId={routeId} startSpawn={startSpawn} />
    </Canvas>
  );
});
